import { useTranslation } from 'react-i18next';
import { BrowserRouter, Navigate, Outlet, Route, Routes } from "react-router-dom";
import { useAuthContext } from "./providers/AuthProvider";
import Application from "./screens/Application";
import ApplicationAttachments from "./screens/ApplicationAttachments";
import ApplicationDetails, { ApplicationOverview } from "./screens/ApplicationDetails";
import { ApplicationFormContainer } from "./screens/ApplicationForm";
import ApplicationMedia, { MediaContainer, UploadMedia } from "./screens/ApplicationMedia";
import ApplicationMessages from "./screens/ApplicationMessages";
import ApplicationReportDetails from "./screens/ApplicationReportDetails";
import ApplicationReports, { ApplicationReportsOverview } from "./screens/ApplicationReports";
import AuthDashboard from "./screens/AuthDashboard";
import Cookies from "./screens/Cookies";
import Dashboard, { Archive, Overview, Profile } from "./screens/Dashboard";
import EmailVerification from "./screens/EmailVerification";
import FirstLogin from "./screens/FirstLogin";
import Form from "./screens/Form";
import Privacy from "./screens/Privacy";
import Report from "./screens/Report";
import ReportForm from "./screens/ReportForm";
import ReportFormContainer from "./screens/ReportFormContainer";
import ReportSection from "./screens/ReportSection";
import Section from "./screens/Section";
import SubmittedScreen from "./screens/SubmittedScreen";
import ApplicationAccess, { AccessContainer, NewAccess } from './screens/ApplicationAccess';
import AcceptAccessInvitation from './screens/AcceptAccessInvitation';
import ApplicationAgreements, { Agreement, AgreementContainer } from './screens/ApplicationAgreements';
import { ApplicationRequestsOverview } from './screens/ApplicationReports';
import Playground, { PlaygroundDone, PlaygroundGuard } from './screens/Playground';

function resolveBasename(): string {
    const p = window.location.pathname;
    // Only apply VITE_BASE_PATH when the current URL is actually under that prefix.
    // Using it unconditionally causes React Router's stripBasename() to return null
    // for any URL that doesn't start with the basename, leading to null.replace() crashes.
    const envBase = (import.meta.env.VITE_BASE_PATH as string | undefined)?.replace(/\/$/, '');
    if (envBase && (p === envBase || p.startsWith(envBase + '/'))) return envBase;
    // Legacy runtime detection for /v* and /beta deployments
    if (p.startsWith('/v')) return '/' + p.split('/')[1];
    if (p.startsWith('/beta')) return '/beta';
    return '';
}

const AppContainer = () => {
    const { t } = useTranslation();
    const base = resolveBasename();

    return (
        <BrowserRouter basename={base}>
            <Routes>
                <Route path="/" element={<Navigate to="/din-side" replace />} />

                <Route path="/personvern" element={<Privacy />} />
                <Route path="/informasjonskapsler" element={<Cookies />} />
                <Route path="/invitasjon/:hash" element={<AcceptAccessInvitation />} />

                <Route path="/" element={<ProtectedRoute />}>
                    <Route path="/din-side" element={<Dashboard />}>
                        <Route index element={<Overview />} />
                        <Route path="arkiv" element={<Archive />} />
                        <Route path="rediger-profil" element={<Profile />} />
                        <Route path="lekeplass" element={<Playground />} />
                    </Route>

                    {/* Lekeplassen: samme skjermer som søkeren ser, men
                        providerne kjører i sandkasse og lagrer ingenting. */}
                    <Route path="/lekeplass" element={<PlaygroundGuard><Outlet /></PlaygroundGuard>}>
                        <Route path="soknad" element={<ApplicationFormContainer sandbox />}>
                            <Route path=":formType" element={<Application />}>
                                <Route path=":section" element={<Section />}>
                                    <Route path=":form" element={<Form />} />
                                </Route>
                            </Route>
                        </Route>
                        {/* `:id` er søknadstypen rapportbetingelsene evalueres mot (0 = ingen). */}
                        <Route path="rapport/:id" element={<ReportFormContainer sandbox />}>
                            <Route path=":formType" element={<Report />}>
                                <Route path=":section" element={<ReportSection />}>
                                    <Route path=":form" element={<ReportForm />} />
                                </Route>
                            </Route>
                        </Route>
                        <Route path="ferdig" element={<PlaygroundDone />} />
                    </Route>

                    <Route path="soknad/:id" element={<ApplicationDetails />}>
                        <Route index element={<ApplicationOverview />} />
                        <Route path="filer" element={<ApplicationAttachments />} />
                        <Route path="meldinger" element={<ApplicationMessages />} />
                        <Route path="rapporter" element={<ApplicationReports />}>
                            <Route index element={<ApplicationReportsOverview />} />
                            <Route path=":reportId/detaljer" element={<ApplicationReportDetails />} />
                        </Route>
                        <Route path="forespørsler" element={<ApplicationReports />}>
                            <Route index element={<ApplicationRequestsOverview />} />
                            <Route path=":reportId/detaljer" element={<ApplicationReportDetails />} />
                        </Route>
                        <Route path="del-historien" element={<MediaContainer />}>
                            <Route index element={<ApplicationMedia />} />
                            <Route path=":mediaType" element={<UploadMedia />} />
                        </Route>
                        <Route path="avtaler" element={<ApplicationAgreements />} />
                        <Route path="avtaler/:agreement" element={<AgreementContainer />}>
                            <Route index element={<Agreement />} />
                        </Route>
                        <Route path="tilgangsstyring" element={<AccessContainer />}>
                            <Route index element={<ApplicationAccess />} />
                            <Route path="ny" element={<NewAccess />} />
                        </Route>
                    </Route>

                    <Route path="/soknadskjema" element={<ApplicationFormContainer />}>
                        <Route path=":formType" element={<Application />}>
                            <Route path=":section" element={<Section />}>
                                <Route path=":form" element={<Form />} />
                            </Route>
                        </Route>
                        <Route path="soknad-innsendt" element={
                            <SubmittedScreen
                                title={t('application_submitted_title')}
                                text={t('application_submitted_text')}
                            />}
                        />
                    </Route>

                    <Route path="/rapport/:id" element={<ReportFormContainer />}>
                        <Route path=":formType" element={<Report />}>
                            <Route path=":section" element={<ReportSection />}>
                                <Route path=":form" element={<ReportForm />} />
                            </Route>
                        </Route>
                        <Route path="rapport-innsendt" element={
                            <SubmittedScreen
                                title={t('report_submitted_title')}
                                text={t('report_submitted_text')}
                            />}
                        />
                    </Route>
                </Route>

                <Route path="*" element={<Navigate to="/din-side" />} />
            </Routes>
        </BrowserRouter>
    )
};

const ProtectedRoute = () => {
    const { accessToken, user } = useAuthContext();

    if (!accessToken) {
        return <AuthDashboard />;
    }

    if (!user?.bankid) {
        return <FirstLogin />;
    }

    if (!user?.verified_email) {
        return <EmailVerification />;
    }

    return <Outlet />;
};

export default AppContainer;